import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, Row, Tooltip, message } from 'antd'
import classNames from 'classnames'
import { useAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyForm } from '~/components/ui/MyForm'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyModal } from '~/components/ui/MyModal'
import { MySelect, MySelectDarkDropdown } from '~/components/ui/MySelect'
import {
  AiArtworkOptionTypeEnum,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_adStyleDecorationOptions,
} from '~/graphqls/types'
import { uniqueFunc } from '~/utils'
import styles from '../AiForm.module.css'
import { OptionsTabs } from '../OptionsTabs'
import decoStyles from './StyleModelComp.module.css'
import { styleDecorationAtom } from './atom'
import useOptions from './useOptions'

type StyleDecorationOptionProps = Partial<
  Omit<
    getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_adStyleDecorationOptions,
    '__typename'
  >
>

const StyleDecorationItem: React.FC<{
  isActive: boolean
  isDisabled: boolean
  onChecked: () => void
  data: StyleDecorationOptionProps
}> = ({ isActive, isDisabled, onChecked, data }) => {
  return (
    <div
      className={classNames(
        'rounded-full text-overflow py-1 px-4 text-neutral-800 font-semibold text-sm border-2 text-center cursor-pointer',
        [[isActive ? 'border-neutral-500' : 'border-transparent']],
        {
          'cursor-not-allowed': isDisabled && !isActive,
        }
      )}
      style={{
        background: isActive
          ? 'radial-gradient(74.76% 334.54% at 49.84% 50%, #374176 0%, rgba(55, 65, 118, 0) 100%)'
          : '#23262F',
      }}
      onClick={onChecked}
      title={data.name!}
    >
      {data.name}
    </div>
  )
}

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 7
const max = 10

export const StyleDecorationComp = () => {
  const [styleDecoration, setStyleDecoration] = useAtom(styleDecorationAtom)
  const { allDecorationOptions, allDecorationOptionsLoading } = useOptions()

  const [visible, setVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [target, setTarget] = useState<any | null>()
  const [searchKey, setSearchKey] = useState('')

  const { defaultDecorationOptions, styleDecorationOptions } = useMemo(() => {
    const _options = allDecorationOptions.filter(it => it?.category === '默认')
    const filterDefaultOptions = allDecorationOptions.filter(
      it => it?.category !== '默认'
    )

    let defaultDecorationOptions = filterDefaultOptions
    if (_options.length > 0) {
      defaultDecorationOptions = _options
    }
    return {
      defaultDecorationOptions,
      styleDecorationOptions: filterDefaultOptions,
    }
  }, [allDecorationOptions])

  const tabs = Array.from(
    new Set(
      styleDecorationOptions
        ?.filter(item => !!item?.category)
        ?.map(item => item?.category!)
    )
  )

  const isDisabled = styleDecoration.length >= max

  const onChecked = useCallback(
    (item: StyleDecorationOptionProps, isActive: boolean) => {
      console.log(item, isActive)
      if (isActive) {
        setStyleDecoration(current =>
          current.filter(it => it.key?.trim() !== item.key?.trim())
        )
      } else {
        if (styleDecoration.length >= max) return
        // 校验choiceKey
        const duplicated =
          item.type === AiArtworkOptionTypeEnum.IMAGE_TYPE_MODEL_TAG_COMPLEX // 组合标签
            ? styleDecoration.find(
                s =>
                  s.type ===
                  AiArtworkOptionTypeEnum.IMAGE_TYPE_MODEL_TAG_COMPLEX
              )
            : styleDecoration.find(s => s.choiceKey === item.choiceKey)
        if (!!duplicated) {
          const name =
            styleDecorationOptions.find(o => o?.key === duplicated.key)?.name ??
            ''
          message.error(`该标签无法和${name}同时选择`)
          return
        }
        setStyleDecoration(current => [
          ...current,
          {
            type: item.type as AiArtworkOptionTypeEnum,
            key: item.key!,
            choiceKey: item.choiceKey ?? 0,
          },
        ])
      }
    },
    [styleDecoration, max, styleDecorationOptions]
  )

  const defaultOptions = uniqueFunc(
    [
      ...styleDecoration
        .map(item => {
          return {
            ...styleDecorationOptions.find(
              o => o?.key?.trim() === item.key?.trim()
            ),
          }
        })
        .filter(it => !!it.key),
      ...defaultDecorationOptions?.slice(0, DEFAULT_SHOW_COUNT),
    ],
    'key'
  )

  /**
   * 标签选择选项变了，过滤掉当前选中的，但是不在标签选择选项中的；
   */
  useEffect(() => {
    if (allDecorationOptionsLoading) return
    setStyleDecoration(
      styleDecoration.filter(
        it =>
          styleDecorationOptions.findIndex(
            s => s?.key?.trim() === it.key?.trim()
          ) > -1
      )
    )
  }, [styleDecorationOptions, allDecorationOptionsLoading])

  useEffect(() => {
    // 滚动到 target 位置
    // console.log(target)
    setSearchKey(target?.name ?? '')
    if (!target) return
    const categoryList = styleDecorationOptions.filter(
      item => !item?.category || item.category === target?.category
    )
    const rowIndex = Math.floor(
      categoryList.findIndex(
        option => !!option?.key && option?.key == target?.key
      ) / 4
    )
    const scrollTop = rowIndex * 48
    const row = document.getElementById('styleModelGrid')
    // console.log('%d, %d, %d', currentIndex, rowIndex, scrollTop)
    if (!!row) {
      const maxScroll = Math.max(0, row.scrollHeight - row.offsetHeight)
      row.scrollTop = Math.min(scrollTop, maxScroll)
    }
    // setTarget(() => null)
  }, [target, styleDecorationOptions])

  return (
    <>
      <MyForm.Item
        label={
          <span>
            标签选择{' '}
            <Tooltip
              placement="topLeft"
              arrowPointAtCenter
              title="不同模型拥有不同的模型标签，通过标签选择结合画面描述词可获得更好的作画效果"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </span>
        }
        className={classNames(styles.borderLessFormItem, '!mb-5')}
      >
        <Row gutter={12}>
          {defaultOptions.map(item => {
            const currentItem = styleDecoration.find(
              (it: any) => (it.key?.trim() as string) === item.key?.trim()
            )
            const isActive =
              styleDecoration.findIndex(
                (it: any) => (it.key?.trim() as string) === item.key?.trim()
              ) > -1
            return (
              <Col span={6} key={item.key} className="mb-3">
                <StyleDecorationItem
                  isActive={isActive}
                  isDisabled={isDisabled}
                  data={{ ...item, ...currentItem }}
                  onChecked={() => {
                    onChecked(item, isActive)
                  }}
                />
              </Col>
            )
          })}
          <Col span={6} className=" mb-3">
            <div
              className="rounded-full p-1 text-neutral-800 font-semibold text-sm border-2 border-neutral-300 text-center cursor-pointer flex items-center justify-center"
              style={{
                background: 'rgba(35, 38, 47, 0.32)',
              }}
              onClick={() => setVisible(true)}
            >
              <MyIcon icon={'more1'} className="font-normal" />
              <span className="ml-0.5">更多</span>
            </div>
          </Col>
        </Row>
      </MyForm.Item>
      <MyModal
        open={visible}
        title={
          <div className="flex items-center">
            <>
              标签选择 ({styleDecoration.length}/{max})
            </>
            <div className="relative items-center">
              <MySelect
                className={decoStyles.select}
                $dark={true}
                $rounded={true}
                placeholder="搜索"
                dropdownStyle={{
                  borderRadius: 12,
                  background: '#23262F',
                  padding: '8px 0',
                }}
                value={target?.name}
                showSearch
                filterOption={(input, option) => {
                  return (option?.value ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }}
                dropdownRender={menu => {
                  return <MySelectDarkDropdown>{menu}</MySelectDarkDropdown>
                }}
                onSelect={(val: any) => {
                  const target = styleDecorationOptions.find(
                    option => option?.name === val
                  )
                  if (!!target) {
                    const nextTabIndex = tabs.findIndex(
                      tab => tab === target?.category
                    )
                    if (nextTabIndex >= 0) {
                      setCurrentIndex(nextTabIndex)
                      setTarget(target)
                      if (
                        !styleDecoration.find(it => it.key === target!!.key)
                      ) {
                        onChecked(target, false)
                      }
                    }
                  }
                }}
                onSearch={(val: any) => {
                  setSearchKey(val)
                }}
                suffixIcon={
                  searchKey.length <= 0 && (
                    <MyIcon
                      icon="search"
                      className="text-neutral-400 text-[20px]"
                    ></MyIcon>
                  )
                }
                style={{
                  marginLeft: 16,
                  width: 167,
                }}
              >
                {styleDecorationOptions.map(option => (
                  <MySelect.Option key={option?.key} value={option?.name}>
                    {option?.name}
                  </MySelect.Option>
                ))}
              </MySelect>
              {searchKey.length > 0 && (
                <div
                  id="gridSelectSearchClear"
                  className="absolute right-[11px] top-[4px]"
                  onClick={() => {
                    setTarget(null)
                  }}
                >
                  <MyIcon
                    icon="close"
                    className="text-neutral-400 text-[20px]"
                  />
                </div>
              )}
            </div>
          </div>
        }
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={1440}
        footer={null}
        isFullScreen
        centered
      >
        <OptionsTabs
          options={tabs.map(item => {
            return {
              label: item,
            }
          })}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />

        <div
          id={'styleModelGrid'}
          className="flex content-start mb-3 flex-wrap min-h-[100px] overflow-y-auto scrollbar gap-3"
          style={{
            height: `calc(100vh - 216px)`, // 216px为其他元素的高度+间距
          }}
        >
          {styleDecorationOptions
            .filter(
              item => !item?.category || item.category === tabs[currentIndex]
            )
            .map(item => {
              const currentItem = styleDecoration.find(
                (it: any) => (it.key?.trim() as string) === item?.key?.trim()
              )
              const isActive =
                styleDecoration.findIndex(
                  (it: any) => (it.key?.trim() as string) === item?.key?.trim()
                ) > -1
              return (
                <div
                  key={item?.key}
                  style={{
                    width: 'calc((100% - 60px) / 6)',
                  }}
                >
                  <StyleDecorationItem
                    isDisabled={isDisabled}
                    isActive={isActive}
                    data={{ ...item, ...currentItem }}
                    onChecked={() => {
                      onChecked(item!, isActive)
                    }}
                  />
                </div>
              )
            })}
        </div>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setStyleDecoration([])
              setVisible(false)
            }}
          >
            重置
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
